import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Edit, Package, Tag, Eye, ShoppingCart, Users, Clock, TrendingUp } from 'lucide-react';
import { fetchProductById, clearSelectedProduct } from '../../../../features/productSlice';
import Loader from '../../../../components/Loader';
import API from '../../../../util/API';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const hashString = (input) => {
  const str = String(input || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const formatCompactNumber = (n) => {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  if (num < 1000) return String(num);
  if (num < 1_000_000) return `${(num / 1000).toFixed(1)}K`;
  return `${(num / 1_000_000).toFixed(1)}M`;
};

const formatMoney = (amount, currency) => {
  if (amount === undefined || amount === null || amount === '') return '—';
  const c = String(currency || '').trim();
  return c ? `${c} ${amount}` : String(amount);
};

export default function ProductViewPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedProduct, status, error } = useSelector((state) => state.products);

  const settingsCountry = useSelector((state) => state.userSettings?.shipToCountry) || 'BD';
  const qs = new URLSearchParams(location.search);
  const viewCountry = (qs.get('country') || settingsCountry || 'BD').toUpperCase();
  const viewCurrency = 'USD';

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [viewStats, setViewStats] = useState(null);
  const [viewStatsLoading, setViewStatsLoading] = useState(false);
  const [productOrders, setProductOrders] = useState([]);
  const [productOrdersTotal, setProductOrdersTotal] = useState(0);
  const [productOrdersLoading, setProductOrdersLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchProductById({ id, country: viewCountry, currency: viewCurrency }));
    return () => {
      dispatch(clearSelectedProduct());
    };
  }, [dispatch, id, viewCountry, viewCurrency]);

  const title = useMemo(() => {
    return (
      selectedProduct?.name ||
      selectedProduct?.sku?.sku_attr ||
      'Untitled Product'
    );
  }, [selectedProduct]);

  const images = useMemo(() => {
    const direct = asArray(selectedProduct?.images).filter(Boolean);
    if (direct.length) return direct;

    const skuProps = asArray(selectedProduct?.sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o)
      .map((p) => p?.sku_image)
      .filter(Boolean);
    return skuProps;
  }, [selectedProduct]);

  const aliProductId = String(selectedProduct?.productId || selectedProduct?.product_id || '').trim();
  const editId = aliProductId || String(selectedProduct?._id || id || '').trim();

  useEffect(() => {
    let cancelled = false;
    if (!aliProductId) {
      setViewStats(null);
      return () => {};
    }

    (async () => {
      try {
        setViewStatsLoading(true);
        const res = await API.get(`/analytics/product/${encodeURIComponent(aliProductId)}/views?days=30`);
        if (cancelled) return;
        setViewStats(res?.data?.data || null);
      } catch {
        if (cancelled) return;
        setViewStats(null);
      } finally {
        if (cancelled) return;
        setViewStatsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aliProductId]);

  useEffect(() => {
    let cancelled = false;
    if (!aliProductId) {
      setProductOrders([]);
      setProductOrdersTotal(0);
      return () => {};
    }

    (async () => {
      try {
        setProductOrdersLoading(true);
        const res = await API.get(`/order/admin/by-product/${encodeURIComponent(aliProductId)}?page=1&pageSize=50`);
        if (cancelled) return;
        const payload = res?.data;
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        setProductOrders(rows);
        setProductOrdersTotal(Number(payload?.total) || rows.length || 0);
      } catch {
        if (cancelled) return;
        setProductOrders([]);
        setProductOrdersTotal(0);
      } finally {
        if (cancelled) return;
        setProductOrdersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [aliProductId]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [aliProductId]);

  const skus = useMemo(() => asArray(selectedProduct?.skus), [selectedProduct]);
  const properties = useMemo(() => asArray(selectedProduct?.property), [selectedProduct]);
  const description = String(selectedProduct?.description || '').trim();

  const skuImageRows = useMemo(() => {
    const rows = [];
    const seen = new Set();
    for (const sku of skus) {
      const props = asArray(sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o);
      const imgProp = props.find((p) => p?.sku_image);
      const img = String(imgProp?.sku_image || '').trim();
      if (!img) continue;

      const key = `${String(sku?.sku_id || '')}::${img}`;
      if (seen.has(key)) continue;
      seen.add(key);

      rows.push({
        sku_id: String(sku?.sku_id || '').trim(),
        sku_attr: String(sku?.sku_attr || '').trim(),
        image: img,
      });
    }
    return rows;
  }, [skus]);

  const priceText = useMemo(() => {
    const p = selectedProduct?.price ?? selectedProduct?.sku?.offer_sale_price;
    if (p === undefined || p === null || p === '') return '—';
    const c = String(selectedProduct?.currency || selectedProduct?.sku?.converted_currency || viewCurrency || '').trim();
    return c ? `${c} ${p}` : String(p);
  }, [selectedProduct, viewCurrency]);

  const viewOnAliHref = aliProductId ? `https://www.aliexpress.com/item/${aliProductId}.html` : '';
  const editHref = editId
    ? `/administrator/products/editProduct/${encodeURIComponent(editId)}?country=${encodeURIComponent(viewCountry)}&currency=${encodeURIComponent(viewCurrency)}`
    : '';

  const analytics = useMemo(() => {
    const seed = hashString(`${aliProductId || id}-${viewCountry}-${viewCurrency}`);
    const visitorsTotal = 800 + (seed % 3500);
    const pageViewsTotal = visitorsTotal + 300 + (seed % 1800);
    const avgTimeSeconds = 45 + (seed % 220);

    const todayVisitors = 8 + (seed % 45);

    const dailyVisitors = Array.from({ length: 7 }).map((_, idx) => {
      const v = 30 + ((seed + idx * 97) % 120);
      return { day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx], visitors: v };
    });

    const topReferrers = [
      { name: 'Direct', value: 42 + (seed % 10) },
      { name: 'Search', value: 28 + (seed % 8) },
      { name: 'Social', value: 12 + (seed % 6) },
      { name: 'Ads', value: 8 + (seed % 6) },
    ];

    return {
      visitorsTotal,
      pageViewsTotal,
      avgTimeSeconds,
      todayVisitors,
      dailyVisitors,
      topReferrers,
    };
  }, [aliProductId, id, selectedProduct, viewCountry, viewCurrency]);

  const visitorsTotal = viewStats?.uniqueVisitors ?? analytics.visitorsTotal;
  const pageViewsTotal = viewStats?.totalViews ?? analytics.pageViewsTotal;

  const ordersTotal = productOrdersLoading ? null : (Number(productOrdersTotal) || 0);
  const todayOrders = useMemo(() => {
    if (productOrdersLoading) return null;
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    return (asArray(productOrders) || []).filter((o) => {
      const dt = new Date(o?.createdAt);
      if (Number.isNaN(dt.getTime())) return false;
      return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    }).length;
  }, [productOrders, productOrdersLoading]);

  const conversionRate = visitorsTotal > 0 && typeof ordersTotal === 'number'
    ? (ordersTotal / visitorsTotal) * 100
    : 0;

  const mainImage = images[activeImageIndex] || images[0] || '';

  if (status === 'loading') {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="p-6 space-y-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {typeof error === 'string' ? error : (error?.message || error?.error || 'Failed to load product')}
        </div>
      </div>
    );
  }

  if (!selectedProduct || Object.keys(selectedProduct).length === 0) {
    return (
      <div className="p-6 space-y-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          Product not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
            <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900 truncate">{title}</h2>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-mono font-black text-slate-700">
              {viewCountry} / {viewCurrency}
            </span>
            <span className="text-slate-500 font-mono break-all">
              Ali ID: {aliProductId || '—'}
            </span>
            <span className="text-slate-400">•</span>
            <span className="text-slate-500">Updated: {formatDateTime(selectedProduct?.updatedAt || selectedProduct?.createdAt)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {viewOnAliHref ? (
            <a
              href={viewOnAliHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
              title="View on AliExpress"
            >
              <ExternalLink className="w-4 h-4" />
              AliExpress
            </a>
          ) : null}

          {editHref ? (
            <Link
              to={editHref}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-sm font-black text-white hover:opacity-95"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Link>
          ) : null}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Images (user-like gallery) */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" /> Images
            </h3>

            {mainImage ? (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <img
                      src={mainImage}
                      alt={title}
                      className="w-full h-[320px] md:h-[420px] object-contain bg-white"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>

                <div className="md:col-span-1">
                  <div className="grid grid-cols-5 md:grid-cols-1 gap-2">
                    {images.map((img, idx) => (
                      <button
                        key={`${img}-${idx}`}
                        type="button"
                        onClick={() => setActiveImageIndex(idx)}
                        className={cx(
                          'rounded-xl border overflow-hidden bg-white aspect-square',
                          idx === activeImageIndex ? 'border-primary' : 'border-slate-200 hover:border-slate-300'
                        )}
                        title={`Image ${idx + 1}`}
                      >
                        <img
                          src={img}
                          alt={`${title} ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600 text-center">
                No images available.
              </div>
            )}

            {skuImageRows.length ? (
              <div className="mt-5">
                <p className="text-xs font-black uppercase tracking-wide text-slate-500">SKU images</p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {skuImageRows.slice(0, 25).map((row) => (
                    <div
                      key={`${row.sku_id || 'sku'}-${row.image}`}
                      className="rounded-2xl border border-slate-200 bg-white p-3"
                      title={row.sku_attr || row.sku_id || 'SKU'}
                    >
                      <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden aspect-square">
                        <img
                          src={row.image}
                          alt={row.sku_attr || row.sku_id || title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="mt-2">
                        <div className="text-[11px] font-mono font-black text-slate-700 break-all">{row.sku_id || '—'}</div>
                        <div className="text-[11px] text-slate-600 break-words line-clamp-2">{row.sku_attr || '—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>

          {/* Overview */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">Overview</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold text-slate-500">Price</p>
                <p className="mt-1 text-lg font-black text-slate-900">{priceText}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold text-slate-500">SKUs</p>
                <p className="mt-1 text-lg font-black text-slate-900">{skus.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold text-slate-500">Exchange rate</p>
                <p className="mt-1 text-lg font-black text-slate-900">
                  {selectedProduct?.rate !== undefined && selectedProduct?.rate !== null ? String(selectedProduct.rate) : '—'}
                </p>
              </div>

              {description ? (
                <div className="md:col-span-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">Description</p>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words">{description}</p>
                </div>
              ) : null}
            </div>
          </Card>

          {/* Analytics */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">Analytics</h3>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-500">Visitors</p>
                  <Eye className="w-4 h-4 text-primary" />
                </div>
                <p className="mt-1 text-xl font-black text-slate-900">{formatCompactNumber(visitorsTotal)}</p>
                <p className="mt-1 text-xs text-slate-500">Today: {analytics.todayVisitors}{viewStatsLoading ? ' (loading...)' : ''}</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-500">Page views</p>
                  <Users className="w-4 h-4 text-primary" />
                </div>
                <p className="mt-1 text-xl font-black text-slate-900">{formatCompactNumber(pageViewsTotal)}</p>
                <p className="mt-1 text-xs text-slate-500">Avg time: {Math.floor(analytics.avgTimeSeconds / 60)}m {analytics.avgTimeSeconds % 60}s</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-500">Orders</p>
                  <ShoppingCart className="w-4 h-4 text-primary" />
                </div>
                <p className="mt-1 text-xl font-black text-slate-900">{productOrdersLoading ? '…' : formatCompactNumber(ordersTotal)}</p>
                <p className="mt-1 text-xs text-slate-500">Today: {productOrdersLoading ? '…' : String(todayOrders ?? 0)}</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-slate-500">Conversion</p>
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <p className="mt-1 text-xl font-black text-slate-900">{conversionRate.toFixed(2)}%</p>
                <p className="mt-1 text-xs text-slate-500">Orders / Visitors</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-slate-500">Visitors (last 7 days)</p>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-end gap-3 h-28">
                    {analytics.dailyVisitors.map((d) => {
                      const max = Math.max(...analytics.dailyVisitors.map((x) => x.visitors));
                      const h = max ? Math.round((d.visitors / max) * 100) : 0;
                      return (
                        <div key={d.day} className="flex-1">
                          <div className="rounded-xl bg-slate-100 h-24 flex items-end overflow-hidden">
                            <div className="w-full bg-primary/70" style={{ height: `${h}%` }} />
                          </div>
                          <div className="mt-2 text-[11px] font-bold text-slate-600 text-center">{d.day}</div>
                          <div className="text-[11px] text-slate-500 text-center">{d.visitors}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-black uppercase tracking-wide text-slate-500">Top referrers</p>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                  {analytics.topReferrers.map((r) => (
                    <div key={r.name} className="flex items-center gap-3">
                      <div className="w-24 text-sm font-black text-slate-800">{r.name}</div>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-primary/70" style={{ width: `${clamp(r.value, 0, 100)}%` }} />
                      </div>
                      <div className="w-10 text-right text-xs font-bold text-slate-600">{r.value}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-xs font-black uppercase tracking-wide text-slate-500">Orders containing this product</p>
              <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-600">
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Order</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Customer</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Country</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Qty</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Total</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {productOrdersLoading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-slate-500 text-center">Loading orders…</td>
                      </tr>
                    ) : productOrders.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-slate-500 text-center">No orders found for this product.</td>
                      </tr>
                    ) : (
                      productOrders.map((o) => {
                        const orderLabel = o?.orderId || o?._id || '—';
                        const user = o?.user;
                        const customerName =
                          user?.name ||
                          user?.username ||
                          [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                          '—';
                        const customerEmail = user?.email || '';
                        const country = o?.shippingAddress?.countryCode || o?.shippingAddress?.country || '—';
                        const qty = o?.matchedQty ?? 0;
                         const total = o?.totalPrice;
                         const currency = o?.paidCurrency || o?.currency || viewCurrency;
                         const usdValueCandidate =
                           o?.accountingUsd?.amount !== null &&
                           o?.accountingUsd?.amount !== undefined &&
                           o?.accountingUsd?.amount !== ''
                             ? o.accountingUsd.amount
                             : o?.baseTotalPrice;
                         const hasBaseTotal = Number.isFinite(Number(usdValueCandidate));
                         const usdValueLabel =
                           o?.accountingUsd?.classification === 'current_rate_estimate'
                             ? 'estimate'
                             : o?.accountingUsd?.classification === 'manual_correction'
                               ? 'corrected'
                               : 'base';
                        const statusLabel = o?.statusLabel || o?.externalStatus || '—';

                        return (
                          <tr key={o?._id || orderLabel} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <Link
                                to={`/administrator/orders/details/${encodeURIComponent(String(o?._id || ''))}`}
                                className="font-mono text-xs font-black text-primary hover:underline break-all"
                                title="View order"
                              >
                                {String(orderLabel)}
                              </Link>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-black text-slate-900">{String(customerName)}</div>
                              {customerEmail ? <div className="text-xs text-slate-500">{String(customerEmail)}</div> : null}
                            </td>
                            <td className="px-4 py-3 font-black text-slate-900">{String(country)}</td>
                            <td className="px-4 py-3 font-black text-slate-900">{String(qty)}</td>
                            <td className="px-4 py-3">
                              <p className="font-black text-slate-900">{formatMoney(total, currency)}</p>
                               {hasBaseTotal ? (
                                 <p className="mt-0.5 text-xs font-semibold text-slate-500">
                                   {formatMoney(Number(usdValueCandidate), 'USD')} {usdValueLabel}
                                 </p>
                              ) : o?.isPaid && currency !== 'USD' ? (
                                <p className="mt-0.5 text-xs font-semibold text-amber-700">
                                  Historical USD rate unavailable
                                </p>
                              ) : null}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-black text-slate-700">
                                <Clock className="w-3.5 h-3.5 text-primary" /> {String(statusLabel)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700">{formatDateTime(o?.createdAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {/* SKUs */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">SKUs ({skus.length})</h3>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-600">
                    <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">SKU ID</th>
                    <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">SKU Attr</th>
                    <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Sale Price</th>
                    <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Price</th>
                    <th className="px-4 py-3 text-xs font-black tracking-wide uppercase">Bulk</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {skus.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-slate-500 text-center">No SKUs found</td>
                    </tr>
                  ) : (
                    skus.map((s, idx) => (
                      <tr key={`${s?.sku_id || idx}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs font-black text-slate-700 break-all">{String(s?.sku_id || '—')}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700 break-all">{String(s?.sku_attr || '—')}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{String(s?.offer_sale_price ?? '—')}</td>
                        <td className="px-4 py-3 text-slate-700">{String(s?.sku_price ?? '—')}</td>
                        <td className="px-4 py-3 text-slate-700">{String(s?.offer_bulk_sale_price ?? '—')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Details + Properties */}
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">Details</h3>
            <div className="mt-4 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-bold text-slate-500">Basic</p>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-slate-500">Country</span>
                    <span className="font-black text-slate-900">{String(selectedProduct?.country || viewCountry || '—')}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-slate-500">Currency</span>
                    <span className="font-black text-slate-900">{String(selectedProduct?.currency || viewCurrency || '—')}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-slate-500">Product ID</span>
                    <span className="font-mono text-xs font-black text-slate-900 break-all">{aliProductId || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="text-slate-500">Created</span>
                    <span className="font-semibold text-slate-900">{formatDateTime(selectedProduct?.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h4 className="text-sm font-black tracking-tight text-slate-900 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary" /> Properties ({properties.length})
                </h4>

                {properties.length ? (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {properties.slice(0, 48).map((p, idx) => (
                      <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="text-xs font-bold text-slate-500">{String(p?.attr_name || p?.name || p?.property_name || 'Property')}</div>
                        <div className="mt-1 text-sm font-semibold text-slate-900 break-words">
                          {String(p?.attr_value || p?.value || p?.property_value || '—')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-slate-500">No properties available.</div>
                )}
              </div>

              <details className="rounded-2xl border border-slate-200 bg-white p-4">
                <summary className="cursor-pointer text-sm font-black tracking-tight text-slate-900">Raw JSON</summary>
                <pre className="mt-3 max-h-[480px] overflow-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-[11px] leading-relaxed text-slate-800">
                  {JSON.stringify(selectedProduct, null, 2)}
                </pre>
              </details>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">Summary</h3>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Price</span>
                <span className="font-black text-slate-900">{priceText}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">SKUs</span>
                <span className="font-black text-slate-900">{skus.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Properties</span>
                <span className="font-black text-slate-900">{properties.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Rate</span>
                <span className="font-black text-slate-900">
                  {selectedProduct?.rate !== undefined && selectedProduct?.rate !== null ? String(selectedProduct.rate) : '—'}
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-black tracking-tight text-slate-900">Quick</h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-500">Visitors</p>
                <p className="mt-1 text-base font-black text-slate-900">{formatCompactNumber(visitorsTotal)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-500">Orders</p>
                <p className="mt-1 text-base font-black text-slate-900">{productOrdersLoading ? '…' : formatCompactNumber(ordersTotal)}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
